import { photoApi } from '@/lib/api/photo.api'
import { createLogger } from '@/lib/logger'

import { SessionChangedError } from '@scrambl/shared/session-scope'

import { requestDedup } from './request-deduplication.service'

const logger = createLogger('lib/services/profile-image-cache.service')

interface CacheEntry {
  imageUrl: string | null
  timestamp: number
  /** Overrides CACHE_TTL — a failed lookup is only remembered briefly. */
  ttl?: number
  promise?: Promise<string | null>
}

interface BatchRequest {
  userIds: string[]
  promise: Promise<Record<string, string | null>>
  timestamp: number
}

interface BatchWaiter {
  userIds: string[]
  result: Record<string, string | null>
  resolve: (result: Record<string, string | null>) => void
  /** Already re-queued once after its request was superseded. */
  retried?: boolean
}

/**
 * A request dropped because the workspace session moved on (or the fetch was
 * aborted) says nothing about the user's photo, so it must not be cached.
 */
function isSupersededRequest(error: unknown): boolean {
  return (
    error instanceof SessionChangedError ||
    (error instanceof Error && error.name === 'AbortError')
  )
}

export class ProfileImageCacheService {
  private cache: Map<string, CacheEntry> = new Map()
  private batchRequests: Map<string, BatchRequest> = new Map()
  private pendingUserIds: Set<string> = new Set()
  private batchWaiters: BatchWaiter[] = []
  private batchTimer: NodeJS.Timeout | null = null

  // Configuration
  private readonly CACHE_TTL = 10 * 60 * 1000 // 10 minutes (increased for better performance)
  // A failed lookup shows initials briefly, not for the whole CACHE_TTL.
  private readonly ERROR_TTL = 30 * 1000
  private readonly BATCH_DELAY = 5 // 5ms delay to collect requests (reduced further)
  // Server caps `/users/profile-images/bulk` at 50 IDs per request — must stay in sync.
  private readonly MAX_BATCH_SIZE = 50

  /**
   * Get profile images for multiple users
   * This method batches requests automatically for efficiency
   */
  async getProfileImages(
    userIds: string[],
  ): Promise<Record<string, string | null>> {
    const result: Record<string, string | null> = {}
    const uncachedIds: string[] = []
    const now = Date.now()

    // Check cache first
    for (const userId of userIds) {
      const cached = this.cache.get(userId)

      if (cached && now - cached.timestamp < (cached.ttl ?? this.CACHE_TTL)) {
        result[userId] = cached.imageUrl
      } else {
        uncachedIds.push(userId)
        this.pendingUserIds.add(userId)
      }
    }

    // If all are cached, return immediately
    if (uncachedIds.length === 0) {
      return result
    }

    // Check if there's an existing batch request that includes our users
    for (const batch of this.batchRequests.values()) {
      if (uncachedIds.every(id => batch.userIds.includes(id))) {
        // Wait for existing batch
        const batchResult = await batch.promise
        for (const userId of uncachedIds) {
          result[userId] = batchResult[userId] || null
        }
        return result
      }
    }

    // Schedule one shared batch flush. Every caller registers a waiter so a
    // later avatar request cannot cancel an earlier caller's resolution.
    return new Promise(resolve => {
      this.batchWaiters.push({ userIds: uncachedIds, result, resolve })

      if (!this.batchTimer) {
        this.batchTimer = setTimeout(() => {
          void this.flushBatch()
        }, this.BATCH_DELAY)
      }
    })
  }

  private async flushBatch(): Promise<void> {
    this.batchTimer = null
    const waiters = this.batchWaiters.splice(0)

    await this.executeBatch()

    for (const waiter of waiters) {
      // A superseded request left no cache entry: ask once more rather than
      // settle the avatar on initials until it remounts.
      if (!waiter.retried && waiter.userIds.some(id => !this.cache.has(id))) {
        for (const userId of waiter.userIds) {
          if (!this.cache.has(userId)) this.pendingUserIds.add(userId)
        }
        this.batchWaiters.push({ ...waiter, retried: true })
        if (!this.batchTimer) {
          this.batchTimer = setTimeout(() => {
            void this.flushBatch()
          }, this.BATCH_DELAY)
        }
        continue
      }
      for (const userId of waiter.userIds) {
        const cached = this.cache.get(userId)
        waiter.result[userId] = cached?.imageUrl || null
      }
      waiter.resolve(waiter.result)
    }
  }

  /**
   * Get a single profile image with deduplication
   */
  async getProfileImage(userId: string): Promise<string | null> {
    // Use request deduplication to prevent multiple simultaneous requests for the same user
    return requestDedup.dedupe(
      `profile-image-${userId}`,
      async () => {
        const result = await this.getProfileImages([userId])
        return result[userId] || null
      },
      1000, // Cache for 1 second to handle rapid re-renders
    )
  }

  /**
   * Execute pending batch requests
   */
  private async executeBatch(): Promise<void> {
    if (this.pendingUserIds.size === 0) return

    // Convert pending IDs to array and clear the set
    const userIds = Array.from(this.pendingUserIds)
    this.pendingUserIds.clear()

    // Split into smaller batches if needed
    const batches: string[][] = []
    for (let i = 0; i < userIds.length; i += this.MAX_BATCH_SIZE) {
      batches.push(userIds.slice(i, i + this.MAX_BATCH_SIZE))
    }

    // Execute all batches in parallel
    const batchPromises = batches.map(async batchUserIds => {
      const batchKey = batchUserIds.join(',')

      // Create batch request
      const batchPromise = photoApi
        .getBulkProfileImages(batchUserIds)
        .then(images => {
          const now = Date.now()

          // Update cache with results
          for (const userId of batchUserIds) {
            this.cache.set(userId, {
              imageUrl: images[userId] || null,
              timestamp: now,
            })
          }

          return images
        })
        .catch(error => {
          if (isSupersededRequest(error)) return {}
          logger.error('[ProfileImageCache] Batch request failed:', error)

          // Remember the failure briefly so a down API isn't hammered, but
          // short enough that photos come back soon after it recovers.
          const now = Date.now()
          for (const userId of batchUserIds) {
            this.cache.set(userId, {
              imageUrl: null,
              timestamp: now,
              ttl: this.ERROR_TTL,
            })
          }

          return {}
        })
        .finally(() => {
          // Clean up batch request
          this.batchRequests.delete(batchKey)
        })

      // Store batch request
      this.batchRequests.set(batchKey, {
        userIds: batchUserIds,
        promise: batchPromise,
        timestamp: Date.now(),
      })

      return batchPromise
    })

    await Promise.all(batchPromises)
  }

  /**
   * Invalidate cache for specific users
   */
  invalidateUsers(userIds: string[]): void {
    for (const userId of userIds) {
      this.cache.delete(userId)
    }
  }

  /**
   * Clear entire cache
   */
  clearCache(): void {
    this.cache.clear()
    this.batchRequests.clear()
    this.pendingUserIds.clear()

    if (this.batchTimer) {
      clearTimeout(this.batchTimer)
      this.batchTimer = null
    }

    // Clearing a scheduled batch must not strand mounted avatar components.
    const waiters = this.batchWaiters.splice(0)
    for (const waiter of waiters) {
      for (const userId of waiter.userIds) {
        waiter.result[userId] = null
      }
      waiter.resolve(waiter.result)
    }
  }

  /**
   * Preload profile images for users
   * Useful when you know you'll need these images soon
   */
  async preloadImages(userIds: string[]): Promise<void> {
    await this.getProfileImages(userIds)
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): {
    size: number
    pendingRequests: number
    batchRequests: number
  } {
    return {
      size: this.cache.size,
      pendingRequests: this.pendingUserIds.size,
      batchRequests: this.batchRequests.size,
    }
  }
}

// Export singleton instance
export const profileImageCache = new ProfileImageCacheService()
